/**
 * Editor для doc.retail_sales (Порція Роздріб.5b) — бухгалтерський UI.
 *
 * НЕ для POS — POS UI mobile-first це Порція 6.
 * Тут — повний редактор чека для виправлення/back-dated проводок.
 *
 * Особливості:
 *  - items таблиця (HTML inline) з типом goods/service
 *  - customer_card lookup за card_code
 *  - client-side підрахунок subtotal/total
 *  - is_backdated checkbox — для бух-операцій заднім числом
 *  - 4 actions: Зберегти, Провести, Фіскалізувати, Скасувати проводку
 *
 * auto_post семантика (рішення власника β):
 *   is_backdated=false → backend auto-POST у тій самій транзакції;
 *   is_backdated=true → чернетка, окремий /post.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { nomenclature as nomApi, orgs } from '@/api/catalogs';
import {
  retailSales,
  type ItemKind,
  type RetailPaymentMethod,
  type RetailSaleItemIn,
} from '@/api/retailSales';
import { retailCustomerCards, type CustomerCardLookup } from '@/api/retailCustomerCards';
import { retailPosWorkstations } from '@/api/retailPosWorkstations';
import { retailSalesChannels } from '@/api/retailSalesChannels';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import RequirePermission from '@/auth/RequirePermission';
import { validateRequired } from '@/utils/validateRequired';
import { extractApiError } from '@/utils/extractApiError';
import RequiredLabel from '@/components/RequiredLabel';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import DatePopover from '@/components/DatePopover';

interface ItemRow extends RetailSaleItemIn {
  // локальний key для React при додаванні нових рядків
  _key: string;
}

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  pos_workstation_id: string;
  sales_channel_id: string;
  customer_card_id: string;
  card_code: string;
  payment_method: RetailPaymentMethod;
  cash_amount: string;
  card_amount: string;
  bonus_writeoff_amount: string;
  change_amount: string;
  discount_amount: string;
  is_backdated: boolean;
  backdate_reason: string;
  items: ItemRow[];
  comment: string;
}

const EMPTY: FormState = {
  organization_id: '',
  document_number: '',
  document_date: '',
  pos_workstation_id: '',
  sales_channel_id: '',
  customer_card_id: '',
  card_code: '',
  payment_method: 'cash',
  cash_amount: '0',
  card_amount: '0',
  bonus_writeoff_amount: '0',
  change_amount: '0',
  discount_amount: '0',
  is_backdated: false,
  backdate_reason: '',
  items: [],
  comment: '',
};

const newItem = (line: number): ItemRow => ({
  _key: crypto.randomUUID(),
  line_number: line,
  nomenclature_id: '',
  item_kind: 'goods',
  quantity: '1',
  price_per_unit: '0',
  discount_percent: '0',
  discount_amount: '0',
  vat_rate: '20.00',
});

export default function RetailSaleEditor() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const { availableOrgs } = usePermissionsByOrg('retail.sale.create');
  const nomQ = useQuery({
    queryKey: ['nomenclature-full'],
    queryFn: () => nomApi.list({ limit: 1000 }),
  });

  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [foundCard, setFoundCard] = useState<CustomerCardLookup | null>(null);

  const wsQ = useQuery({
    queryKey: ['retail-pos-workstations-for-sale', form.organization_id],
    queryFn: () => retailPosWorkstations.list({ organization_id: form.organization_id, limit: 500 }),
    enabled: !!form.organization_id,
  });
  const channelsQ = useQuery({
    queryKey: ['retail-sales-channels-for-sale', form.organization_id],
    queryFn: () => retailSalesChannels.list({ organization_id: form.organization_id, limit: 500 }),
    enabled: !!form.organization_id,
  });

  const docQuery = useQuery({
    queryKey: ['retail-sale', id],
    queryFn: () => retailSales.get(id!),
    enabled: !isNew,
  });

  const numberPreview = useNumberPreview('doc.retail_sales', form.organization_id, isNew);

  useEffect(() => {
    if (isNew && availableOrgs.length > 0 && !form.organization_id) {
      setForm((f) => ({ ...f, organization_id: availableOrgs[0]!.id }));
    }
  }, [isNew, availableOrgs, form.organization_id]);

  useEffect(() => {
    if (!isNew && docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        pos_workstation_id: d.pos_workstation_id,
        sales_channel_id: d.sales_channel_id ?? '',
        customer_card_id: d.customer_card_id ?? '',
        card_code: '',
        payment_method: d.payment_method,
        cash_amount: d.cash_amount,
        card_amount: d.card_amount,
        bonus_writeoff_amount: d.bonus_writeoff_amount,
        change_amount: d.change_amount,
        discount_amount: d.discount_amount,
        is_backdated: d.is_backdated,
        backdate_reason: d.backdate_reason ?? '',
        items: d.items.map((it) => ({
          _key: it.id,
          line_number: it.line_number,
          nomenclature_id: it.nomenclature_id,
          item_kind: it.item_kind as ItemKind,
          quantity: it.quantity,
          unit_id: it.unit_id ?? undefined,
          price_per_unit: it.price_per_unit,
          price_group_id: it.price_group_id ?? undefined,
          discount_percent: it.discount_percent ?? '0',
          discount_amount: it.discount_amount ?? '0',
          vat_rate: it.vat_rate ?? '20.00',
        })),
        comment: d.comment ?? '',
      });
    }
  }, [isNew, docQuery.data]);

  const posted = !!docQuery.data?.posted;
  const fiscalStatus = docQuery.data?.fiscal_status;
  const status = docQuery.data?.status;

  // Client-side підрахунок subtotal/total для UI preview.
  const subtotal = useMemo(() => {
    return form.items.reduce(
      (sum, it) =>
        sum +
        Number(it.quantity || 0) * Number(it.price_per_unit || 0) -
        Number(it.discount_amount || 0),
      0,
    );
  }, [form.items]);

  const total =
    subtotal - Number(form.discount_amount || 0) - Number(form.bonus_writeoff_amount || 0);

  const lookupCard = async () => {
    setError(null);
    setFoundCard(null);
    if (!form.organization_id || !form.card_code) {
      setError('Заповніть код картки');
      return;
    }
    try {
      const c = await retailCustomerCards.lookup({
        organization_id: form.organization_id,
        card_code: form.card_code,
      });
      setFoundCard(c);
      setForm((f) => ({ ...f, customer_card_id: c.id }));
    } catch (e) {
      setError(extractApiError(e));
    }
  };

  const addItem = () => {
    const nextLine = form.items.length > 0 ? Math.max(...form.items.map((i) => i.line_number)) + 1 : 1;
    setForm({ ...form, items: [...form.items, newItem(nextLine)] });
  };

  const removeItem = (key: string) => {
    setForm({ ...form, items: form.items.filter((i) => i._key !== key) });
  };

  const updateItem = (key: string, patch: Partial<ItemRow>) => {
    setForm({
      ...form,
      items: form.items.map((i) => (i._key === key ? { ...i, ...patch } : i)),
    });
  };

  const payload = (): Parameters<typeof retailSales.create>[0] => ({
    organization_id: form.organization_id,
    document_number: form.document_number || null,
    document_date: form.document_date ? new Date(form.document_date).toISOString() : null,
    pos_workstation_id: form.pos_workstation_id,
    sales_channel_id: form.sales_channel_id || null,
    customer_card_id: form.customer_card_id || null,
    payment_method: form.payment_method,
    cash_amount: form.cash_amount,
    card_amount: form.card_amount,
    bonus_writeoff_amount: form.bonus_writeoff_amount,
    change_amount: form.change_amount,
    discount_amount: form.discount_amount,
    is_backdated: form.is_backdated,
    backdate_reason: form.backdate_reason || null,
    comment: form.comment || null,
    items: form.items.map(({ _key: _drop, ...rest }) => rest),
  });

  const create = useMutation({
    mutationFn: () => retailSales.create(payload()),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['retail-sales'] });
      navigate(`/retail/retail-sales/${doc.id}`);
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const update = useMutation({
    mutationFn: () => retailSales.update(id!, payload()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['retail-sales'] });
      qc.invalidateQueries({ queryKey: ['retail-sale', id] });
    },
    onError: (e) => setError(extractApiError(e)),
  });

  // Новий: створити та провести одним кліком (create → post).
  const createAndPost = useMutation({
    mutationFn: async () => {
      const doc = await retailSales.create(payload());
      await retailSales.post(doc.id);
      return doc;
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['retail-sales'] });
      navigate(`/retail/retail-sales/${doc.id}`);
    },
    onError: (e) => setError(extractApiError(e)),
  });

  // Збережений: зберегти правки та провести одним кліком (update → post).
  const updateAndPost = useMutation({
    mutationFn: async () => {
      await retailSales.update(id!, payload());
      await retailSales.post(id!);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['retail-sale', id] });
      qc.invalidateQueries({ queryKey: ['retail-sales'] });
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const unpost = useMutation({
    mutationFn: () => retailSales.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['retail-sale', id] });
      qc.invalidateQueries({ queryKey: ['retail-sales'] });
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const fiscalize = useMutation({
    mutationFn: () => retailSales.fiscalize(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['retail-sale', id] });
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const voidSale = useMutation({
    mutationFn: () => retailSales.void(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['retail-sale', id] });
      qc.invalidateQueries({ queryKey: ['retail-sales'] });
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const remove = useMutation({
    mutationFn: () => retailSales.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['retail-sales'] });
      navigate('/retail/retail-sales');
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const canUnpost = posted && fiscalStatus !== 'fiscalized' && fiscalStatus !== 'pending';

  function validate(): boolean {
    setError(null);
    const err = validateRequired(
      {
        organization_id: form.organization_id,
        pos_workstation_id: form.pos_workstation_id,
      },
      {
        organization_id: 'Юрособа',
        pos_workstation_id: 'POS-станція',
      },
    );
    if (err) { setError(err); return false; }
    if (form.items.length === 0) {
      setError('Додайте позиції перед проведенням');
      return false;
    }
    return true;
  }

  const handleSave = () => {
    if (!validate()) return;
    if (isNew) create.mutate();
    else update.mutate();
  };
  // Новий: зберегти та провести.
  const handleSaveAndPost = () => {
    if (!validate()) return;
    createAndPost.mutate();
  };
  // Збережений: зберегти правки та провести.
  const handleUpdateAndPost = () => {
    if (!validate()) return;
    updateAndPost.mutate();
  };

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew
            ? `Новий чек ${numberPreview ? `(${numberPreview})` : ''}`
            : `Чек ${form.document_number}`}
          {posted && (
            <span style={{ marginLeft: '0.75rem', color: '#15803d', fontSize: '1rem' }}>
              ✓ Проведено
            </span>
          )}
          {fiscalStatus === 'fiscalized' && (
            <span style={{ marginLeft: '0.5rem', color: '#1d4ed8', fontSize: '1rem' }}>
              📋 Фіскалізовано
            </span>
          )}
          {fiscalStatus === 'failed' && (
            <span style={{ marginLeft: '0.5rem', color: '#b91c1c', fontSize: '1rem' }}>
              ⚠ ПРРО помилка
            </span>
          )}
          {status === 'voided' && (
            <span style={{ marginLeft: '0.5rem', color: '#6b7280', fontSize: '1rem' }}>
              ⊘ Анульовано
            </span>
          )}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Стандартні дії документа (до списку / зберегти / провести / розпровести / видалити).
              Фіскалізація/анулювання — фіскальні дії retail, лишаються окремими кнопками поряд. */}
          <DocumentToolbar
            onBackToList={() => navigate('/retail/retail-sales')}
            onSave={handleSave}
            saveDisabled={
              create.isPending || update.isPending || createAndPost.isPending
              || form.items.length === 0 || status === 'voided'
            }
            isPosted={posted}
            isNew={isNew}
            postable={isNew ? true : canUnpost || (!posted && status !== 'voided')}
            onPost={isNew ? handleSaveAndPost : handleUpdateAndPost}
            postDisabled={
              create.isPending || update.isPending || createAndPost.isPending
              || updateAndPost.isPending || form.items.length === 0
            }
            postTitle="Зберігає поточні зміни та проводить документ"
            onUnpost={() => unpost.mutate()}
            unpostPending={unpost.isPending}
            onDelete={() => {
              if (window.confirm('Видалити чернетку?')) remove.mutate();
            }}
            deletePending={remove.isPending}
          />
          {/* Фіскальні дії retail (поза стандартним тулбаром документа). */}
          {!isNew && posted && !form.is_backdated && fiscalStatus !== 'fiscalized' && (
            <RequirePermission code="retail.sale.fiscalize">
              <button onClick={() => fiscalize.mutate()} disabled={fiscalize.isPending}>
                фіскалізувати
              </button>
            </RequirePermission>
          )}
          {!isNew && posted && status !== 'voided' && (
            <RequirePermission code="retail.sale.void">
              <button
                className="danger"
                onClick={() => {
                  if (window.confirm('Анулювати чек?')) voidSale.mutate();
                }}
              >
                анулювати
              </button>
            </RequirePermission>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <RequiredLabel>Організація</RequiredLabel>
          {isNew ? (
            <select
              value={form.organization_id}
              onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
            >
              <option value="">—</option>
              {availableOrgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.code} — {o.name}
                </option>
              ))}
            </select>
          ) : (
            <div>
              {orgList.data?.items.find((o) => o.id === form.organization_id)?.name ??
                form.organization_id}
            </div>
          )}
        </div>
        <div className="field">
          <label>Дата чека</label>
          <DatePopover
            withTime
            value={form.document_date}
            onChange={(v) => setForm({ ...form, document_date: v })}
            disabled={posted}
          />
        </div>
        <div className="field">
          <RequiredLabel>POS-станція</RequiredLabel>
          <select
            value={form.pos_workstation_id}
            onChange={(e) => setForm({ ...form, pos_workstation_id: e.target.value })}
            disabled={!isNew}
          >
            <option value="">—</option>
            {wsQ.data?.items.map((w) => (
              <option key={w.id} value={w.id}>
                {w.code} — {w.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Канал продажу (опц.)</label>
          <select
            value={form.sales_channel_id}
            onChange={(e) => setForm({ ...form, sales_channel_id: e.target.value })}
            disabled={posted}
          >
            <option value="">—</option>
            {channelsQ.data?.items.map((c) => (
              <option key={c.id} value={c.id}>
                {c.code} — {c.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label>Картка клієнта (опц.)</label>
          {form.customer_card_id && !isNew ? (
            <div>{foundCard?.holder_name ?? `Картка ID: ${form.customer_card_id.slice(0, 8)}…`}</div>
          ) : (
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <input
                value={form.card_code}
                onChange={(e) => setForm({ ...form, card_code: e.target.value })}
                placeholder="Код картки"
                style={{ flex: 1 }}
                disabled={posted}
              />
              <button onClick={lookupCard} disabled={posted}>Знайти</button>
              {foundCard && (
                <small style={{ color: '#15803d', alignSelf: 'center' }}>
                  ✓ {foundCard.holder_name ?? '(без імені)'} • баланс:{' '}
                  {Number(foundCard.bonus_balance).toFixed(2)}
                </small>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Items таблиця */}
      <h3 style={{ marginTop: '1.5rem' }}>Позиції чека</h3>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid #cbd5e1', background: '#f1f5f9' }}>
            <th style={{ padding: '0.5rem', textAlign: 'left', width: 50 }}>#</th>
            <th style={{ padding: '0.5rem', textAlign: 'left' }}>Номенклатура</th>
            <th style={{ padding: '0.5rem', textAlign: 'left', width: 120 }}>Тип</th>
            <th style={{ padding: '0.5rem', textAlign: 'right', width: 110 }}>К-сть</th>
            <th style={{ padding: '0.5rem', textAlign: 'right', width: 120 }}>Ціна</th>
            <th style={{ padding: '0.5rem', textAlign: 'right', width: 120 }}>Знижка</th>
            <th style={{ padding: '0.5rem', textAlign: 'right', width: 90 }}>ПДВ%</th>
            <th style={{ padding: '0.5rem', textAlign: 'right', width: 130 }}>Сума</th>
            <th style={{ padding: '0.5rem', width: 50 }}></th>
          </tr>
        </thead>
        <tbody>
          {form.items.map((it) => {
            const rowSubtotal =
              Number(it.quantity || 0) * Number(it.price_per_unit || 0) -
              Number(it.discount_amount || 0);
            return (
              <tr key={it._key} style={{ borderBottom: '1px solid #e2e8f0' }}>
                <td style={{ padding: '0.5rem' }}>{it.line_number}</td>
                <td style={{ padding: '0.5rem' }}>
                  <select
                    value={it.nomenclature_id}
                    onChange={(e) => updateItem(it._key, { nomenclature_id: e.target.value })}
                    disabled={posted}
                    style={{ width: '100%' }}
                  >
                    <option value="">—</option>
                    {nomQ.data?.items.map((n) => (
                      <option key={n.id} value={n.id}>
                        {n.code} — {n.name}
                      </option>
                    ))}
                  </select>
                </td>
                <td style={{ padding: '0.5rem' }}>
                  <select
                    value={it.item_kind}
                    onChange={(e) =>
                      updateItem(it._key, { item_kind: e.target.value as ItemKind })
                    }
                    disabled={posted}
                  >
                    <option value="goods">Товар</option>
                    <option value="service">Послуга</option>
                  </select>
                </td>
                <td style={{ padding: '0.5rem' }}>
                  <input
                    type="number"
                    step="0.001"
                    value={it.quantity}
                    onChange={(e) => updateItem(it._key, { quantity: e.target.value })}
                    disabled={posted}
                    style={{ width: '100%', textAlign: 'right' }}
                  />
                </td>
                <td style={{ padding: '0.5rem' }}>
                  <input
                    type="number"
                    step="0.01"
                    value={it.price_per_unit ?? ''}
                    onChange={(e) => updateItem(it._key, { price_per_unit: e.target.value })}
                    disabled={posted}
                    style={{ width: '100%', textAlign: 'right' }}
                  />
                </td>
                <td style={{ padding: '0.5rem' }}>
                  <input
                    type="number"
                    step="0.01"
                    value={it.discount_amount ?? ''}
                    onChange={(e) => updateItem(it._key, { discount_amount: e.target.value })}
                    disabled={posted}
                    style={{ width: '100%', textAlign: 'right' }}
                  />
                </td>
                <td style={{ padding: '0.5rem' }}>
                  <input
                    type="number"
                    step="0.01"
                    value={it.vat_rate ?? ''}
                    onChange={(e) => updateItem(it._key, { vat_rate: e.target.value })}
                    disabled={posted}
                    style={{ width: '100%', textAlign: 'right' }}
                  />
                </td>
                <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                  {rowSubtotal.toFixed(2)}
                </td>
                <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                  {!posted && (
                    <button onClick={() => removeItem(it._key)} title="Видалити">
                      ×
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {!posted && (
        <button onClick={addItem} style={{ marginTop: '0.5rem' }}>
          + Додати позицію
        </button>
      )}

      {/* Підсумки */}
      <div
        style={{
          marginTop: '1rem',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '0.75rem',
        }}
      >
        <div>
          <h3>Платіж</h3>
          <div className="field">
            <label>Метод оплати</label>
            <select
              value={form.payment_method}
              onChange={(e) =>
                setForm({ ...form, payment_method: e.target.value as RetailPaymentMethod })
              }
              disabled={posted}
            >
              <option value="cash">Готівка</option>
              <option value="card">Картка</option>
              <option value="mixed">Змішана</option>
              <option value="transfer">Переказ</option>
            </select>
          </div>
          <div className="field">
            <label>Готівкою</label>
            <input
              type="number"
              step="0.01"
              value={form.cash_amount}
              onChange={(e) => setForm({ ...form, cash_amount: e.target.value })}
              disabled={posted}
            />
          </div>
          <div className="field">
            <label>Карткою</label>
            <input
              type="number"
              step="0.01"
              value={form.card_amount}
              onChange={(e) => setForm({ ...form, card_amount: e.target.value })}
              disabled={posted}
            />
          </div>
          <div className="field">
            <label>Списано бонусів</label>
            <input
              type="number"
              step="0.01"
              value={form.bonus_writeoff_amount}
              onChange={(e) => setForm({ ...form, bonus_writeoff_amount: e.target.value })}
              disabled={posted}
            />
            {foundCard && Number(form.bonus_writeoff_amount) > Number(foundCard.bonus_balance) && (
              <small style={{ color: '#b91c1c' }}>
                ⚠ Перевищує баланс картки ({Number(foundCard.bonus_balance).toFixed(2)})
              </small>
            )}
          </div>
          <div className="field">
            <label>Решта (видача)</label>
            <input
              type="number"
              step="0.01"
              value={form.change_amount}
              onChange={(e) => setForm({ ...form, change_amount: e.target.value })}
              disabled={posted}
            />
          </div>
        </div>
        <div>
          <h3>Підсумки</h3>
          <div>
            Підсумок позицій: <strong>{subtotal.toFixed(2)}</strong>
          </div>
          <div className="field">
            <label>Знижка на чек</label>
            <input
              type="number"
              step="0.01"
              value={form.discount_amount}
              onChange={(e) => setForm({ ...form, discount_amount: e.target.value })}
              disabled={posted}
            />
          </div>
          <div style={{ fontSize: '1.25rem', marginTop: '0.5rem' }}>
            <strong>До сплати: {total.toFixed(2)} грн</strong>
          </div>

          <h3 style={{ marginTop: '1rem' }}>Заднім числом</h3>
          <div className="field">
            <label>
              <input
                type="checkbox"
                checked={form.is_backdated}
                onChange={(e) =>
                  setForm({ ...form, is_backdated: e.target.checked })
                }
                disabled={posted}
              />
              {' '}is_backdated — бух-чернетка (без ПРРО)
            </label>
          </div>
          {form.is_backdated && (
            <div className="field">
              <label>Причина (back-date)</label>
              <input
                value={form.backdate_reason}
                onChange={(e) => setForm({ ...form, backdate_reason: e.target.value })}
                disabled={posted}
                placeholder="Внесення раніше пропущеного чека"
              />
            </div>
          )}
        </div>
      </div>

      <div className="field" style={{ marginTop: '1rem' }}>
        <label>Коментар</label>
        <textarea
          value={form.comment}
          onChange={(e) => setForm({ ...form, comment: e.target.value })}
          rows={2}
          disabled={posted}
        />
      </div>

      {error && (
        <div className="error" style={{ marginTop: '1rem', color: '#b91c1c' }}>
          {error}
        </div>
      )}
    </div>
  );
}
